<template>
	<div class="buyers-list">
		<p class="table-title">{{ t("table.buyers") }}</p>
		<header>
			<div class="toolbar">
				<div ref="columnSelectorWrapperRef" class="column-selector-anchor">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="column-selector-toggle"
						@click="toggleColumnSelector"
					>
						{{ t("buyers-list.column-selector.title") }}<span class="count">{{ visibleColumnKeys.length }}</span>
					</Button>
					<ColumnSelector
						v-if="columnSelectorOpen"
						:columns="selectorColumns"
						:default-columns="defaultSelectorColumns"
						:group-labels="groupLabels"
						:title="t('buyers-list.column-selector.title')"
						@save="handleColumnSave"
						@close="columnSelectorOpen = false"
					/>
				</div>
				<div ref="filtersWrapperRef" class="filters-wrapper">
					<Button
						variant="tertiary"
						size="xs"
						shape="pill"
						class="filters-toggle"
						:class="{ active: buyersListFiltersRef?.hasActiveFilters() }"
						@click="toggleFiltersPanel"
					>
						{{ t("opportunities.tabs.filters")
						}}<span v-if="buyersListFiltersRef?.activeFiltersCount" class="count">{{
							buyersListFiltersRef.activeFiltersCount
						}}</span>
					</Button>
					<TableFloatingFilterPanel
						v-show="filtersPanelOpen"
						:title="t('table.buyers')"
						@close="filtersPanelOpen = false"
					>
						<template v-if="buyersListFiltersRef?.hasActiveFilters()" #header-actions>
							<Button
								variant="ghost"
								size="xs"
								shape="pill"
								class="reset-filters-button"
								@click="buyersListFiltersRef?.resetFilters()"
							>
								{{ t("filters.reset-to-default") }}
							</Button>
						</template>
						<BuyersListFilters ref="buyersListFiltersRef" />
					</TableFloatingFilterPanel>
				</div>
				<Button
					:variant="hasContactDetailsFilter ? 'primary' : 'tertiary'"
					size="xs"
					shape="pill"
					class="quick-filter"
					:aria-pressed="hasContactDetailsFilter"
					@click="toggleContactDetailsFilter"
				>
					{{ t("buyers-list.contact-details-available-filter") }}
				</Button>
				<TableSearch
					v-model="nameSearch"
					:placeholder="t('buyers-list.search-placeholder')"
					@click="closeOtherPanels"
				/>
			</div>

			<div class="header-right">
				<span class="total-results"
					><strong>{{ recordsCount }}</strong> {{ t("buyers-list.records-found") }}</span
				>
			</div>
		</header>

		<section class="records">
			<LoadingOverlay :visible="loading" />
			<div class="buyers">
				<table>
					<thead>
						<tr>
							<th
								v-for="col in mainColumns"
								:key="col.key"
								scope="col"
								:class="[col.cssClass, { sortable: sortableColumnFor(col.key) != null }]"
								:data-sort-order="sortableColumnFor(col.key) ? sortOrderFor(sortableColumnFor(col.key)!) : undefined"
								@click="sortableColumnFor(col.key) && toggleSort(sortableColumnFor(col.key)!)"
							>
								<template v-if="col.key === ColumnKey.SelectContact">
									<div class="select-all">
										<label for="select-all-input">{{ t("buyers-list.select-all") }}</label>
										<input
											id="select-all-input"
											type="checkbox"
											:checked="areAllContactsSelected"
											:disabled="!selectableContactIds.length"
											@change="toggleSelectAll"
										/>
									</div>
								</template>
								<template v-else>{{ columnLabel(col.key) }}</template>
							</th>
						</tr>
					</thead>

					<tbody>
						<template v-for="(buyer, index) in buyers" :key="buyer.id || undefined">
							<tr :class="{ expandable: !!buyer.id }" @click="buyer.id && toggleTransaction(buyer.id)">
								<td v-for="col in mainColumns" :key="col.key" :class="col.cssClass">
									<template v-if="col.key === ColumnKey.Name">
										<div class="name-cell">
											<AppIcon
												v-if="buyer.id"
												:name="IconName.ChevronRightSmall"
												class="expand-chevron"
												:class="{ expanded: expandedBuyerId === buyer.id }"
												:size="11"
												@click.stop="toggleTransaction(buyer.id)"
											/>
											<ArkHoverCard v-if="buyer.crmContactId" :positioning="{ placement: 'top', gutter: 6 }">
												<template #trigger>
													<a
														class="link-pill"
														:href="crmContactUrl(buyer.crmContactId)"
														target="_blank"
														rel="noopener noreferrer"
														@click.stop
													>
														<span class="first">{{ buyer.firstName }}</span>
														<span class="last">{{ buyer.lastName }}</span>
														<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
													</a>
												</template>
												<span class="link-pill-hint">{{ t("buyers-list.open-in-hubspot") }}</span>
											</ArkHoverCard>
											<span v-else class="hs-contact">
												<span class="first">{{ buyer.firstName }}</span>
												<span class="last">{{ buyer.lastName }}</span>
											</span>
										</div>
									</template>

									<template v-else-if="col.key === ColumnKey.Company">
										<ArkHoverCard
											v-if="buyer.crmContactId && buyer.crmCompanyId"
											:positioning="{ placement: 'top', gutter: 6 }"
										>
											<template #trigger>
												<a
													class="link-pill"
													:href="crmCompanyUrl(buyer.crmCompanyId)"
													target="_blank"
													rel="noopener noreferrer"
													@click.stop
												>
													{{ buyer.companyName }}
													<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
												</a>
											</template>
											<span class="link-pill-hint">{{ t("buyers-list.open-in-hubspot") }}</span>
										</ArkHoverCard>
										<span v-else class="hs-company">{{ buyer.companyName }}</span>
									</template>

									<template v-else-if="col.key === ColumnKey.Transactions">
										<span class="badge">{{ transactionCountLabel(buyer) }}</span>
									</template>

									<template v-else-if="col.key === ColumnKey.LastTransactionDate">{{
										lastTransactionDate(buyer)
									}}</template>

									<template v-else-if="col.key === ColumnKey.SelectContact">
										<label v-if="buyer.crmContactId" :for="`buyer-selector-${buyer.id || index}`" @click.stop>
											<input
												:id="`buyer-selector-${buyer.id || index}`"
												type="checkbox"
												:checked="selectedContactIds.has(buyer.id)"
												@change="selectBuyer(buyer.id || null, ($event.currentTarget as HTMLInputElement).checked)"
											/>
											<span class="sr-only">{{ t("buyers-list.add-to-list") }}</span>
										</label>
										<div v-else class="selector-empty">
											<span class="badge">{{ t("buyers-list.unavailable") }}</span>
										</div>
									</template>
								</td>
							</tr>
							<tr v-if="buyer.id && expandedBuyerId === buyer.id" class="transaction-details">
								<td :colspan="mainColumns.length">
									<div class="transaction-details-scroll">
										<table class="sub-table">
											<thead>
												<tr>
													<th v-for="col in detailColumns" :key="col.key" scope="col" :class="col.cssClass">
														{{ columnLabel(col.key) }}
													</th>
													<th scope="col" class="map-column"></th>
												</tr>
											</thead>
											<tbody>
												<tr
													v-for="(transaction, txIndex) in getTransactions(buyer)"
													:key="`${buyer.id}-${transaction.inscriptionNumber || txIndex}`"
												>
													<td v-for="col in detailColumns" :key="col.key" :class="col.cssClass">
														<template v-if="col.key === ColumnKey.Inscription">
															<ArkHoverCard :positioning="{ placement: 'top', gutter: 6 }">
																<template #trigger>
																	<a
																		href="#"
																		class="link-pill"
																		@click.prevent="emitTransactionSelection(transaction.inscriptionNumber)"
																	>
																		{{ formatInscriptionNumber(transaction.inscriptionNumber) }}
																		<AppIcon :name="IconName.ArrowUpRight" :size="12" :stroke-width="2" />
																	</a>
																</template>
																<span class="link-pill-hint">{{ t("buyers-list.open-detailed-panel") }}</span>
															</ArkHoverCard>
														</template>
														<template v-else-if="col.key === ColumnKey.Classification">{{
															translateClassification(transaction)
														}}</template>
														<template v-else-if="col.key === ColumnKey.Address">{{
															isCoordinateAddress(transaction.address) ? "" : transaction.address
														}}</template>
														<template v-else-if="col.key === ColumnKey.Price">{{
															formatPrice(transaction.salePrice)
														}}</template>
														<template v-else-if="col.key === ColumnKey.PricePerP2">{{
															formatPricePerArea(transaction.pricePerLandSqft)
														}}</template>
														<template v-else-if="col.key === ColumnKey.PricePerP2Buildable">{{
															formatPricePerArea(transaction.pricePerBuildableSqft)
														}}</template>
														<template v-else-if="col.key === ColumnKey.PricePerUnit">{{
															formatRoundedNumber(transaction.pricePerUnit, 100)
														}}</template>
														<template v-else-if="col.key === ColumnKey.BuildableArea">{{
															formatArea(transaction.buildableAreaSqft)
														}}</template>
														<template v-else-if="col.key === ColumnKey.Units">{{
															formatRoundedNumber(transaction.numberOfUnits, 1)
														}}</template>
													</td>
													<td class="map-column">
														<a
															class="map-link"
															:href="`?transaction=${(transaction.inscriptionNumber || '').replace(/\\D+/g, '')}`"
															target="_blank"
															:title="t('buyers-list.view-on-map')"
														>
															<AppIcon :name="IconName.MapPinSmall" />
														</a>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>
		</section>

		<footer class="table-footer">
			<div v-if="recordsCount > 0" class="pagination">
				<ArkPagination :count="recordsCount" :page="page" @page-change="handlePaginationChange" />
			</div>

			<div class="export">
				<span class="total-results">{{ t("buyers-list.contacts-selected", { count: selectedContactIds.size }) }}</span>
				<input
					id="buyers-list-name"
					v-model.trim="listName"
					type="text"
					:placeholder="t('buyers-list.enter-list-name')"
					:aria-label="t('buyers-list.list-name')"
					@input="listError = ''"
					@keydown.enter.prevent="createList"
				/>
				<Button
					class="submit"
					variant="primary"
					size="m"
					shape="pill"
					@click="createList"
					:disabled="!selectedContactIds.size || !listName || isCreatingList"
					:aria-busy="isCreatingList"
				>
					<span>{{ t("buyers-list.export-to-hubspot") }}</span>
					<span v-if="isCreatingList" class="inline-loader" aria-hidden="true"></span>
				</Button>

				<p v-if="listError" class="error">{{ listError }}</p>
				<a v-if="createdList?.id" :href="crmListUrl(createdList.id)" target="_blank" rel="noopener">
					{{ t("buyers-list.view-hubspot-list") }}
				</a>
			</div>
		</footer>
	</div>
</template>

<script lang="ts" setup>
import { onClickOutside } from "@vueuse/core"
import debounce from "lodash.debounce"
import { storeToRefs } from "pinia"
import { computed, onBeforeUnmount, ref, watch } from "vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { useI18n } from "vue-i18n"
import ArkHoverCard from "@/components/ui/ArkHoverCard.vue"
import ArkPagination from "@/components/ui/ArkPagination.vue"
import Button from "@/components/ui/Button.vue"
import BuyersListFilters from "@/components/filtering/BuyersListFilters.vue"
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue"
import ColumnSelector from "@/components/table/ColumnSelector.vue"
import TableFloatingFilterPanel from "@/components/table/TableFloatingFilterPanel.vue"
import TableSearch from "@/components/table/TableSearch.vue"
import { useView } from "@/composables/useView"
import { useUnitSystem } from "@/composables/useUnitSystem"
import { BuyerField, LandSubcategory, TransactionField } from "@/graphql"
import type { Contact } from "@/model/DataModel"
import { UseRootStore } from "@/model/RootStore"
import { ViewScope } from "@/types"
import { formatDateToISO } from "@/utils/date"
import { formatInscriptionNumber, formatQuantity, isCoordinateAddress, roundTo } from "@/utils/format"

import { analytics } from "@/utils/analytics"
import { AreaUnit } from "@/utils/unitSystem"

const CRM_CONTACT_URL = import.meta.env.VITE_HUBSPOT_CONTACT_ENDPOINT
const CRM_COMPANY_URL = import.meta.env.VITE_HUBSPOT_COMPANY_ENDPOINT
const CRM_LIST_URL = import.meta.env.VITE_HUBSPOT_LIST_ENDPOINT

enum ColumnKey {
	Name = "name",
	Company = "primary-company",
	Transactions = "transactions",
	LastTransactionDate = "last-transaction-date",
	SelectContact = "select-contact",
	// transaction detail sub-table
	Inscription = "inscription",
	Classification = "classification",
	Address = "address",
	Price = "price",
	PricePerP2 = "price-per-p2",
	PricePerP2Buildable = "price-per-p2-buildable",
	PricePerUnit = "price-per-unit",
	BuildableArea = "buildable-area",
	Units = "units",
}

const SORT_KEY_BY_COLUMN: Partial<Record<ColumnKey, SortableColumn>> = {
	[ColumnKey.Transactions]: "transaction-count",
	[ColumnKey.LastTransactionDate]: "last-transaction-date",
}

function sortKeyFor(key: string): SortableColumn | undefined {
	return SORT_KEY_BY_COLUMN[key as ColumnKey]
}

function sortableColumnFor(key: string): SortableColumn | undefined {
	return sortKeyFor(key)
}

enum Section {
	Main = "main",
	TransactionDetails = "transaction-details",
}

type ColumnDef = { key: ColumnKey; cssClass?: string; section: Section; locked?: boolean }

// main table columns are locked visible — required to identify a buyer and drive the export flow
const ALL_COLUMNS: ColumnDef[] = [
	{ key: ColumnKey.Name, cssClass: "name", section: Section.Main, locked: true },
	{ key: ColumnKey.Company, cssClass: "company", section: Section.Main, locked: true },
	{ key: ColumnKey.Transactions, cssClass: "transactions", section: Section.Main, locked: true },
	{ key: ColumnKey.LastTransactionDate, cssClass: "last-transaction-date", section: Section.Main, locked: true },
	{ key: ColumnKey.SelectContact, cssClass: "selector", section: Section.Main, locked: true },

	{ key: ColumnKey.Inscription, cssClass: "inscription", section: Section.TransactionDetails },
	{ key: ColumnKey.Classification, cssClass: "classification", section: Section.TransactionDetails },
	{ key: ColumnKey.Address, cssClass: "address", section: Section.TransactionDetails },
	{ key: ColumnKey.Price, section: Section.TransactionDetails },
	{ key: ColumnKey.PricePerP2, section: Section.TransactionDetails },
	{ key: ColumnKey.PricePerP2Buildable, section: Section.TransactionDetails },
	{ key: ColumnKey.PricePerUnit, section: Section.TransactionDetails },
	{ key: ColumnKey.BuildableArea, section: Section.TransactionDetails },
	{ key: ColumnKey.Units, section: Section.TransactionDetails },
]

type ColumnSelection = {
	buyer: BuyerField[]
	transaction: TransactionField[]
}

const COLUMN_SELECT: Record<ColumnKey, ColumnSelection> = {
	[ColumnKey.Name]: { buyer: [BuyerField.FirstName, BuyerField.LastName, BuyerField.CrmContactId], transaction: [] },
	[ColumnKey.Company]: { buyer: [BuyerField.CompanyName, BuyerField.CrmCompanyId], transaction: [] },
	[ColumnKey.Transactions]: { buyer: [BuyerField.Transactions], transaction: [] },
	[ColumnKey.LastTransactionDate]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.DeedOfSaleDate] },
	[ColumnKey.SelectContact]: { buyer: [BuyerField.CrmContactId], transaction: [] },
	[ColumnKey.Inscription]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.InscriptionNumber] },
	[ColumnKey.Classification]: {
		buyer: [BuyerField.Transactions],
		transaction: [TransactionField.AssetType, TransactionField.LandCategoryNew, TransactionField.LandSubcategory],
	},
	[ColumnKey.Address]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.Address] },
	[ColumnKey.Price]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.SalePrice] },
	[ColumnKey.PricePerP2]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.PricePerLandSqft] },
	[ColumnKey.PricePerP2Buildable]: {
		buyer: [BuyerField.Transactions],
		transaction: [TransactionField.PricePerBuildableSqft],
	},
	[ColumnKey.PricePerUnit]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.PricePerUnit] },
	[ColumnKey.BuildableArea]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.BuildableAreaSqft] },
	[ColumnKey.Units]: { buyer: [BuyerField.Transactions], transaction: [TransactionField.NumberOfUnits] },
}

type TransactionSummary = {
	inscriptionNumber?: string
	address?: string
	salePrice?: number
	assetType?: string[]
	landCategoryNew?: string[]
	landSubcategory?: string[]
	pricePerLandSqft?: number
	pricePerBuildableSqft?: number
	pricePerUnit?: number
	buildableAreaSqft?: number
	numberOfUnits?: number
	deedOfSaleDate?: string
}

type PaginationChangeDetails = {
	page: number
	pageSize: number
	offset: number
}

const props = withDefaults(
	defineProps<{
		buyers?: Contact[]
		loading?: boolean
		page?: number
		total?: number
	}>(),
	{
		buyers: () => [],
		loading: false,
		page: 1,
	}
)

const emit = defineEmits<{
	(e: "transaction-selection", inscriptionNumber: string | null | undefined): void
	(e: "page-change", details: PaginationChangeDetails): void
}>()

const store = UseRootStore()
const { currentUser } = storeToRefs(store)
const buyersView = useView(ViewScope.BuyersTable)
const { t } = useI18n()
const { areaUnitSymbol, pricePerAreaUnitSymbol, convertAreaToPreferredUnit, convertPricePerAreaToPreferredUnit } =
	useUnitSystem()

const selectedContactIds = ref(new Set<Contact["id"]>())
const expandedBuyerId = ref<string | null>(null)
const nameSearch = ref(buyersView.view.value.filter?.buyer?.search ?? "")
const commitNameSearch = debounce((value: string) => {
	buyersView.setBuyerSearch(value)
}, 300)

watch(nameSearch, value => commitNameSearch(value))
watch(
	() => buyersView.view.value.filter?.buyer?.search,
	search => {
		const value = search ?? ""

		if (nameSearch.value !== value) {
			nameSearch.value = value
		}
	}
)

onBeforeUnmount(() => commitNameSearch.cancel())

type SortableColumn = "transaction-count" | "last-transaction-date"
const listName = ref("")
const isCreatingList = ref(false)
const listError = ref("")
const createdList = ref<{ id: string } | null>(null)
const filtersPanelOpen = ref(false)
const columnSelectorOpen = ref(false)
const buyersListFiltersRef = ref<InstanceType<typeof BuyersListFilters> | null>(null)
const columnSelectorWrapperRef = ref<HTMLElement | null>(null)
const filtersWrapperRef = ref<HTMLElement | null>(null)
const hasContactDetailsFilter = computed(() => !!buyersView.view.value.filter?.buyer?.hasContactDetails)
const activeViewOrder = computed(() => buyersView.view.value.order?.[0])
const sortColumn = computed<SortableColumn | null>(() => {
	const field = activeViewOrder.value?.field
	return field === "transaction-count" || field === "last-transaction-date" ? field : null
})
const sortOrder = computed<"asc" | "desc">(() => activeViewOrder.value?.direction ?? "asc")

const clickOutsideOptions = { ignore: [".popover-content", ".date-custom-panel"] }
onClickOutside(columnSelectorWrapperRef, () => (columnSelectorOpen.value = false), clickOutsideOptions)
onClickOutside(filtersWrapperRef, () => (filtersPanelOpen.value = false), clickOutsideOptions)

type StoredColumn = { key: string; visible: boolean }

// syncs stored config with ALL_COLUMNS, forcing locked columns visible
const mergedColumns = computed<StoredColumn[]>(() => {
	const select = buyersView.view.value.select
	if (select?.buyer == null && select?.transaction == null) {
		return ALL_COLUMNS.map(column => ({ key: column.key, visible: true }))
	}
	return columnsFromSelectedFields(select?.buyer ?? [], select?.transaction ?? [])
})

const visibleColumnKeys = computed<string[]>(() => mergedColumns.value.filter(c => c.visible).map(c => c.key))

const orderedColumns = computed<ColumnDef[]>(() =>
	visibleColumnKeys.value
		.map(key => ALL_COLUMNS.find(col => col.key === key))
		.filter((col): col is ColumnDef => col != null)
)

const mainColumns = computed(() => orderedColumns.value.filter(col => col.section === Section.Main))
const detailColumns = computed(() => orderedColumns.value.filter(col => col.section === Section.TransactionDetails))

type SelectorColumn = { key: string; label: string; visible: boolean; group: Section; locked?: boolean }

function columnLabel(key: string): string {
	const section = ALL_COLUMNS.find(col => col.key === key)?.section
	const namespace = section === Section.TransactionDetails ? "transaction-columns" : "columns"

	return t(`buyers-list.${namespace}.${key}`, {
		areaUnit: areaUnitSymbol.value,
		pricePerAreaUnit: pricePerAreaUnitSymbol.value,
	})
}

function toSelectorColumns(cols: StoredColumn[]): SelectorColumn[] {
	return cols.map(c => {
		const def = ALL_COLUMNS.find(col => col.key === c.key)!
		return { ...c, label: columnLabel(c.key), group: def.section, locked: def.locked }
	})
}

const selectorColumns = computed<SelectorColumn[]>(() => toSelectorColumns(mergedColumns.value))
const defaultSelectorColumns = computed<SelectorColumn[]>(() =>
	toSelectorColumns(ALL_COLUMNS.map(c => ({ key: c.key, visible: true })))
)

function columnFields(key: string): ColumnSelection {
	return COLUMN_SELECT[key as ColumnKey] ?? { buyer: [], transaction: [] }
}

function selectedColumnFields(columns: { key: string; visible: boolean }[]): ColumnSelection {
	return {
		buyer: [...new Set(columns.filter(column => column.visible).flatMap(column => columnFields(column.key).buyer))],
		transaction: [
			...new Set(columns.filter(column => column.visible).flatMap(column => columnFields(column.key).transaction)),
		],
	}
}

function columnsFromSelectedFields(buyerFields: BuyerField[], transactionFields: TransactionField[]): StoredColumn[] {
	const selectedBuyerFields = new Set(buyerFields)
	const selectedTransactionFields = new Set(transactionFields)
	const visible = ALL_COLUMNS.filter(column => {
		const fields = columnFields(column.key)
		return (
			fields.buyer.every(field => selectedBuyerFields.has(field)) &&
			fields.transaction.every(field => selectedTransactionFields.has(field))
		)
	})
		.map(column => ({
			key: column.key,
			visible: true,
			index: Math.min(
				...columnFields(column.key).buyer.map(field => buyerFields.indexOf(field)),
				...columnFields(column.key).transaction.map(field => transactionFields.indexOf(field))
			),
		}))
		.sort((left, right) => left.index - right.index)
		.map(({ key, visible }) => ({ key, visible }))

	const visibleKeys = new Set(visible.map(column => column.key))
	return [
		...visible,
		...ALL_COLUMNS.filter(column => !visibleKeys.has(column.key)).map(column => ({ key: column.key, visible: false })),
	]
}

const groupLabels = computed<Record<string, string>>(() => ({
	[Section.Main]: t("buyers-list.column-selector.main-section"),
	[Section.TransactionDetails]: t("buyers-list.column-selector.transaction-section"),
}))

function toggleColumnSelector(): void {
	columnSelectorOpen.value = !columnSelectorOpen.value
	filtersPanelOpen.value = false
}

function toggleFiltersPanel(): void {
	filtersPanelOpen.value = !filtersPanelOpen.value
	columnSelectorOpen.value = false
}

function toggleContactDetailsFilter(): void {
	buyersView.setBuyerHasContactDetails(!buyersView.view.value.filter?.buyer?.hasContactDetails)
	closeOtherPanels()
}

function closeOtherPanels(): void {
	columnSelectorOpen.value = false
	filtersPanelOpen.value = false
}

function handleColumnSave(columns: { key: string; visible: boolean }[]): void {
	const selected = selectedColumnFields(columns)
	buyersView.setSelect({
		...buyersView.view.value.select,
		buyer: selected.buyer,
		transaction: selected.transaction,
	})
	columnSelectorOpen.value = false
}

function isString(value: unknown): value is string {
	return typeof value === "string"
}

const recordsCount = computed(() => props.total ?? props.buyers.length)

function sortOrderFor(column: SortableColumn): "asc" | "desc" | undefined {
	return sortColumn.value === column ? sortOrder.value : undefined
}

function toggleSort(column: SortableColumn): void {
	let nextOrder: "asc" | "desc" | null
	if (sortColumn.value !== column) {
		nextOrder = "desc"
	} else if (sortOrder.value === "desc") {
		nextOrder = "asc"
	} else {
		nextOrder = null
	}

	buyersView.setOrder(nextOrder == null ? [] : [{ field: column, direction: nextOrder }])
}
const selectableContactIds = computed(() =>
	props.buyers
		.filter(buyer => isString(buyer.crmContactId))
		.map(buyer => buyer.id)
		.filter(isString)
)
const areAllContactsSelected = computed(
	() =>
		selectableContactIds.value.length > 0 && selectableContactIds.value.every(id => selectedContactIds.value.has(id))
)

function crmContactUrl(id: string) {
	return `${CRM_CONTACT_URL}/${id}`
}
function crmCompanyUrl(id: string) {
	return `${CRM_COMPANY_URL}/${id}`
}
function crmListUrl(id: string) {
	return `${CRM_LIST_URL}/${id}`
}

function getTransactions(buyer: Contact): TransactionSummary[] {
	return Array.isArray(buyer.transactions) ? buyer.transactions : []
}

function transactionCountLabel(buyer: Contact): string {
	const count = buyer.inscriptionNumbers?.length || 0
	return count === 1
		? t("buyers-list.columns.transaction-count-singular", { count })
		: t("buyers-list.columns.transaction-count", { count })
}

function lastTransactionTimestamp(buyer: Contact): number | null {
	const times = getTransactions(buyer)
		.map(transaction => (transaction.deedOfSaleDate ? new Date(transaction.deedOfSaleDate).getTime() : null))
		.filter((time): time is number => time != null && !isNaN(time))

	return times.length === 0 ? null : Math.max(...times)
}

function lastTransactionDate(buyer: Contact): string {
	const timestamp = lastTransactionTimestamp(buyer)
	return timestamp == null ? "" : formatDateToISO(new Date(timestamp))
}

function formatRoundedNumber(value: number | null | undefined, multiple: number): string {
	const rounded = roundTo(value, multiple)
	return rounded == null ? "" : formatQuantity(rounded)
}

function formatArea(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	return formatRoundedNumber(convertAreaToPreferredUnit(value, AreaUnit.SquareFeet), 1)
}

function formatPricePerArea(value: number | null | undefined): string {
	if (value == null || !Number.isFinite(value)) {
		return ""
	}

	return formatRoundedNumber(convertPricePerAreaToPreferredUnit(value, AreaUnit.SquareFeet), 0.01)
}

function formatPrice(value: number | undefined): string {
	const formatted = formatRoundedNumber(value, 0.01)
	return formatted ? `$${formatted}` : ""
}

const landSubcategoryKeyByValue: Record<string, string> = Object.fromEntries(
	Object.entries(LandSubcategory).map(([key, value]) => [value, key])
)

function translateAssetTypes(values: string[] | undefined): string {
	return (values || []).map(value => t(`property.assetType.${value}`)).join(", ")
}

function translateLandCategories(values: string[] | undefined): string {
	return (values || []).map(value => t(`property.landCategory.${value}`)).join(", ")
}

function translateLandSubcategories(values: string[] | undefined): string {
	return (values || [])
		.map(value => t(`property.landSubcategory.${landSubcategoryKeyByValue[value] ?? value}`))
		.join(", ")
}

function translateClassification(transaction: TransactionSummary): string {
	return [
		translateAssetTypes(transaction.assetType),
		translateLandCategories(transaction.landCategoryNew),
		translateLandSubcategories(transaction.landSubcategory),
	]
		.filter(Boolean)
		.join(" · ")
}

function selectBuyer(id: string | null, isAdd: boolean) {
	if (!id) return
	isAdd ? selectedContactIds.value.add(id) : selectedContactIds.value.delete(id)
}

function toggleTransaction(id: string) {
	expandedBuyerId.value = expandedBuyerId.value === id ? null : id
}

function toggleSelectAll() {
	const shouldSelectAll = !areAllContactsSelected.value

	selectableContactIds.value.forEach(id => {
		shouldSelectAll ? selectedContactIds.value.add(id) : selectedContactIds.value.delete(id)
	})
}

async function createList(): Promise<void> {
	const title = listName.value
	if (!title) {
		console.warn("Missing required name for buyers list")
		return
	}
	const buyerIds = Array.from(selectedContactIds.value)
	const userInfo = currentUser.value.getInfo()
	const { firstName, lastName } = userInfo ?? {}
	const name = `[${firstName ?? ""} ${lastName ?? ""}] ${title}`

	if (isCreatingList.value) return

	analytics.buyersListExported({
		userId: store.isUserLoggedIn ? userInfo?.id ?? null : null,
	})

	isCreatingList.value = true
	listError.value = ""
	createdList.value = null

	try {
		const list = await store.createBuyersList(name, Array.from(buyerIds))
		if (list?.id) {
			createdList.value = { id: list.id }
		}
	} catch (error: unknown) {
		const message =
			typeof error === "object" && error !== null && "message" in error
				? String((error as { message?: unknown }).message)
				: String(error)
		listError.value = /List name already exists/i.test(message)
			? t("buyers-list.errors.list-name-exists")
			: t("buyers-list.errors.failed-to-create")
		console.error(error)
	} finally {
		isCreatingList.value = false
	}
}

function emitTransactionSelection(inscriptionNumber: string | null | undefined): void {
	emit("transaction-selection", inscriptionNumber)
}

function handlePaginationChange(details: PaginationChangeDetails): void {
	emit("page-change", details)
}
</script>

<style scoped>
.reset-filters-button {
	white-space: nowrap;
	align-self: center;
	line-height: 1;
}

.buyers-list {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;

	h1 {
		text-transform: capitalize;
	}

	header {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		background-color: var(--neutral-0);
		padding: var(--spacing-sm) var(--spacing-xs);
		margin-bottom: var(--spacing-xs);
		border-radius: var(--border-radius-lg);
		font-weight: var(--font-weight-medium);

		select {
			text-transform: capitalize;
			padding: var(--spacing-sm) var(--spacing-sm);
		}
	}

	header .toolbar {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs);

		.column-selector-anchor {
			position: relative;
		}
	}

	header .header-right {
		display: flex;
		align-items: center;
		gap: var(--spacing-lg);
	}

	header .filters-wrapper {
		position: relative;
	}

	.filters-toggle {
		&.active {
			border-color: var(--neutral-900);
		}
	}

	.filters-toggle,
	.column-selector-toggle {
		.count {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: var(--space-16);
			height: var(--space-16);
			margin-left: var(--spacing-xs);
			padding: 0 var(--spacing-xxs);
			border-radius: var(--border-radius-full);
			background: var(--neutral-200);
			color: var(--neutral-900);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
		}
	}

	header .total-results {
		flex-shrink: 0;
		white-space: nowrap;
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
		color: var(--neutral-600);

		strong {
			font-weight: var(--font-weight-semibold);
		}
	}

	.records {
		position: relative;
		flex: 1;
		min-height: 0;

		.buyers {
			max-height: 100%;
			overflow: auto;
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-lg);

			table {
				background-color: var(--neutral-0);
				width: 100%;
				table-layout: fixed;
				font-size: var(--font-size-small);
				border-collapse: separate;
				border-spacing: 0;

				:is(thead, tbody) tr :is(th, td) {
					padding: var(--space-10);
					border-bottom: var(--border-width-thin) solid var(--neutral-100);
				}

				td.name,
				td.company {
					overflow: hidden;
				}

				td.selector {
					padding: 0;
					overflow: visible;

					label {
						display: flex;
						align-items: center;
						justify-content: flex-end;
						gap: var(--spacing-sm);
						width: 100%;
						padding: var(--space-10);
						cursor: pointer;
						user-select: none;

						input[type="checkbox"] {
							accent-color: var(--primary-green);
						}
					}

					.selector-empty {
						display: flex;
						align-items: center;
						justify-content: flex-end;
						width: 100%;
						padding: var(--space-10);
					}
				}

				thead tr {
					th {
						position: sticky;
						top: 0;
						z-index: 1;
						font-size: var(--font-size-xs);
						font-weight: var(--font-weight-medium);
						text-align: left;
						text-transform: uppercase;
						letter-spacing: 0.02em;
						background-color: var(--neutral-0);
						color: var(--neutral-600);
						border-bottom: var(--border-width-thin) solid var(--neutral-200);

						&.name,
						&.company {
							width: 22%;
						}

						&.transactions {
							width: var(--space-120);
						}

						&.last-transaction-date {
							width: var(--space-120);
						}

						&.sortable {
							cursor: pointer;
							user-select: none;

							&::after {
								content: " ↕";
								opacity: 0.4;
							}

							&:hover::after {
								opacity: 0.7;
							}

							&[data-sort-order="asc"]::after {
								content: " ↑";
								opacity: 0.8;
							}

							&[data-sort-order="desc"]::after {
								content: " ↓";
								opacity: 0.8;
							}
						}

						&.selector {
							width: var(--space-180);
							text-align: right;

							.select-all {
								display: inline-flex;
								align-items: center;
								flex-wrap: nowrap;
								justify-content: flex-end;
								gap: var(--spacing-md);
								white-space: nowrap;
								cursor: pointer;
								user-select: none;

								label {
									font-size: inherit;
									text-transform: inherit;
									letter-spacing: inherit;
									cursor: pointer;
								}

								input[type="checkbox"] {
									accent-color: var(--primary-green);
								}
							}
						}
					}

					.select-all {
						display: flex;
						align-items: center;
						gap: var(--spacing-sm);
						font-weight: var(--font-weight-regular);

						&,
						& > * {
							cursor: pointer;
						}
						label {
							user-select: none;
						}
					}
				}

				tbody {
					background-color: var(--neutral-0);

					tr.expandable {
						cursor: pointer;
					}

					tr:hover {
						background-color: var(--neutral-50);
					}

					.company a.hs-company {
						color: var(--neutral-900);
						text-decoration: none;
					}

					.name {
						.name-cell {
							display: flex;
							align-items: center;
							gap: var(--space-6);

							a.link-pill {
								min-width: 0;
							}
						}

						.expand-chevron {
							flex-shrink: 0;
							color: var(--neutral-600);
							cursor: pointer;
							transition: transform 0.15s ease;

							&.expanded {
								transform: rotate(90deg);
							}
						}

						.hs-contact {
							display: flex;
							gap: var(--spacing-xs);
							flex-wrap: wrap;

							.last {
								text-transform: uppercase;
							}
						}
					}

					.badge {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						padding: 0 var(--spacing-sm);
						height: var(--space-20);
						border-radius: var(--border-radius-full);
						background-color: var(--neutral-100);
						color: var(--neutral-800);
						font-size: var(--font-size-xs);
						font-weight: var(--font-weight-medium);
						white-space: nowrap;
					}
				}
			}
		}
	}

	.transaction-details-scroll {
		overflow-x: auto;
		width: 100%;
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);

		.sub-table {
			width: 100%;
			table-layout: fixed;
			font-size: var(--font-size-small);
			border-collapse: separate;
			border-spacing: 0;

			:is(thead, tbody) tr :is(th, td) {
				padding: var(--spacing-xs) var(--space-10);
				border-bottom: var(--border-width-thin) solid var(--neutral-100);

				&:not(.map-column) {
					overflow: hidden;
					text-overflow: ellipsis;
				}
			}

			:is(th, td) {
				width: var(--space-100);

				&.inscription {
					width: var(--space-120);
				}

				&.address {
					width: var(--space-180);
				}

				&.classification {
					width: var(--space-160);
				}

				&.map-column {
					width: var(--space-48);
				}
			}

			tbody tr :is(td) {
				white-space: nowrap;
			}

			thead th {
				font-size: var(--font-size-xxs);
				font-weight: var(--font-weight-medium);
				line-height: var(--line-height-tight);
				text-align: left;
				text-transform: uppercase;
				letter-spacing: 0.02em;
				white-space: nowrap;
				background-color: var(--neutral-0);
				color: var(--neutral-600);
				border-bottom: var(--border-width-thin) solid var(--neutral-200);
			}

			tbody {
				background-color: var(--neutral-0);

				tr:hover {
					background-color: var(--neutral-50);
				}

				td {
					color: var(--neutral-900);

					&:empty::after {
						content: "—";
					}
				}

				.address {
					white-space: normal;
				}

				.classification {
					white-space: normal;
				}
			}

			.map-column {
				text-align: center;
			}

			.map-link {
				display: inline-flex;
				align-items: center;
				justify-content: center;
				padding: var(--spacing-xs);
				color: var(--neutral-600);
				border: var(--border-width-thin) solid var(--neutral-400);
				border-radius: var(--border-radius-base);

				svg {
					width: var(--size-icon-md);
					height: var(--size-icon-md);
				}

				&:hover {
					color: var(--primary-green);
					border-color: var(--primary-green);
				}
			}
		}
	}

	tr.transaction-details > td {
		padding: 0;
		background-color: var(--neutral-50);
	}

	.table-footer {
		display: flex;
		align-items: center;
		gap: var(--spacing-lg);
		flex-shrink: 0;
		padding: var(--spacing-md) var(--spacing-xs) 0;

		.pagination {
			display: flex;
		}

		.export {
			margin-left: auto;
			display: flex;
			align-items: center;
			flex-wrap: wrap;
			justify-content: flex-end;
			gap: var(--spacing-md);

			.total-results {
				font-size: var(--font-size-small);
			}

			input {
				height: var(--space-28);
				padding: 0 var(--spacing-sm);
				font-family: var(--font-family-primary);
				font-size: var(--font-size-small);
				border: var(--border-width-thin) solid var(--neutral-400);
				border-radius: var(--border-radius-full);
				background: var(--neutral-0);
				color: var(--neutral-900);
				outline: none;
				box-sizing: border-box;

				&:focus {
					border-color: var(--neutral-900);
				}
			}

			.submit {
				gap: var(--spacing-xs);

				.inline-loader {
					width: var(--size-icon-sm);
					height: var(--size-icon-sm);
					border: var(--border-width-base) solid currentColor;
					border-bottom-color: transparent;
					border-radius: var(--border-radius-full);
					display: inline-block;
					animation: rotation 1s linear infinite;
				}
			}

			.error {
				width: 100%;
				margin: 0;
				text-align: right;
				color: var(--accent-warning);
				font-size: var(--font-size-sm);

				&:first-letter {
					text-transform: capitalize;
				}
			}

			a {
				width: 100%;
				text-align: right;
				margin-top: var(--spacing-xxs);
				color: var(--primary-green);
				font-size: var(--font-size-sm);
				text-transform: none;
				text-decoration: underline;
			}
		}
	}
}

@keyframes rotation {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.table-title {
	font-family: var(--font-family-primary);
	font-size: var(--font-size-large);
	font-weight: var(--font-weight-medium);
	color: var(--neutral-900);
	padding: 0;
	margin-bottom: var(--spacing-sm);
	width: fit-content;
}

a.link-pill {
	display: inline-flex;
	align-items: center;
	flex-wrap: wrap;
	max-width: 100%;
	gap: var(--spacing-xxs);
	padding: var(--spacing-xxs) var(--spacing-sm);
	border-radius: var(--border-radius-lg);
	background-color: var(--neutral-100);
	color: var(--neutral-800);
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-medium);
	text-decoration: none;
	transition:
		background-color 0.2s ease,
		color 0.2s ease;

	.last {
		text-transform: uppercase;
	}

	.app-icon {
		flex-shrink: 0;
	}

	&:hover {
		background-color: var(--neutral-200);
		color: var(--neutral-900);
	}
}

.link-pill-hint {
	display: block;
	padding: var(--spacing-xs) var(--spacing-sm);
	font-size: var(--font-size-xs);
	font-weight: var(--font-weight-medium);
	color: var(--neutral-900);
	white-space: nowrap;
}

.sr-only {
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
}
</style>
